/**
 * Sidebar Navigation - Premium slide-in drawer
 *
 * Revision highlights:
 * - Consumes centralized NAV_DESTINATIONS (single source of truth)
 * - Group headers ("Daily", "Library") for scannable hierarchy
 * - Active route: filled icon container + left accent bar + bold label
 * - Drawer width 288px, max 84vw (comfortable on iPhone SE)
 * - Sign-out is visually isolated in footer with extra top spacing
 * - Close button is now ✕ (clearer affordance than a back chevron)
 * - Stagger animation capped at 8 items (avoids long delays)
 * - Backdrop uses pointer-events for safe dismiss without layout hacks
 *
 * Performance:
 * - groupedNav is computed once via module-level groupDestinations()
 * - No inline object allocation in render (styles via stable references)
 * - memo() on component + NavLink handles re-render boundaries
 */

import { useCallback, useEffect, useRef, useState, memo, type CSSProperties } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { X, LogOut } from '@/lib/icons';
import { triggerHaptic } from '@/lib/haptics';
import { useAuthStore } from '@/state/auth';
import { ConfirmModal } from '@/components/ui/Modal';
import {
  groupDestinations,
  isActiveRoute,
  GROUP_LABELS,
  type NavDestination,
  type NavGroup,
} from './navigation';

// ─── Constants ───────────────────────────────────────────────
const ANIMATION_DURATION = 280; // ms - slightly snappier than before
const DRAWER_WIDTH = 288;       // px
const MAX_STAGGER_ITEMS = 8;    // cap stagger animation delay

// Pre-compute grouped navigation (stable, never changes)
const GROUPED_NAV = groupDestinations();

// ─── Component ───────────────────────────────────────────────

interface SidebarNavProps {
  isOpen: boolean;
  onClose: () => void;
  /** Ref to the trigger button for focus restoration on close */
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
}

export const SidebarNav = memo(function SidebarNav({ isOpen, onClose, triggerRef }: SidebarNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuthStore();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);

  // ─── Handlers ────────────────────────────────────────────

  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await signOut();
      onClose();
    } catch {
      // Error handled by store
    } finally {
      setSigningOut(false);
      setShowSignOutConfirm(false);
    }
  }, [signOut, onClose]);

  const handleCloseWithAnimation = useCallback(() => {
    triggerHaptic();
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
      triggerRef?.current?.focus();
    }, ANIMATION_DURATION);
  }, [onClose, triggerRef]);

  const handleNavClick = useCallback(() => {
    triggerHaptic();
  }, []);

  const handleBrandClick = useCallback(() => {
    triggerHaptic();
    navigate('/today');
    handleCloseWithAnimation();
  }, [navigate, handleCloseWithAnimation]);

  // ─── Effects ─────────────────────────────────────────────

  // Track first open
  useEffect(() => {
    if (isOpen && !hasOpened) setHasOpened(true);
  }, [isOpen, hasOpened]);

  // Close on route change
  useEffect(() => {
    if (isOpen) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Focus trap + keyboard
  useEffect(() => {
    if (!isOpen || isClosing) return;
    const focusTimer = setTimeout(() => closeButtonRef.current?.focus(), 80);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCloseWithAnimation();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isClosing]);

  // iOS-safe scroll lock
  useEffect(() => {
    if (!isOpen && !isClosing) return;

    const scrollY = window.scrollY;
    const body = document.body;
    const html = document.documentElement;

    const origBodyOverflow = body.style.overflow;
    const origBodyPosition = body.style.position;
    const origBodyTop = body.style.top;
    const origBodyWidth = body.style.width;
    const origHtmlOverflow = html.style.overflow;

    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    html.style.overflow = 'hidden';

    const preventTouchMove = (e: TouchEvent) => {
      if (sidebarRef.current?.contains(e.target as Node)) return;
      e.preventDefault();
    };
    document.addEventListener('touchmove', preventTouchMove, { passive: false });

    return () => {
      body.style.overflow = origBodyOverflow;
      body.style.position = origBodyPosition;
      body.style.top = origBodyTop;
      body.style.width = origBodyWidth;
      html.style.overflow = origHtmlOverflow;
      window.scrollTo(0, scrollY);
      document.removeEventListener('touchmove', preventTouchMove);
    };
  }, [isOpen, isClosing]);

  // ─── Render guards ───────────────────────────────────────

  if (!isOpen && !isClosing) return null;

  // ─── Styles ──────────────────────────────────────────────

  const drawerStyle: CSSProperties = {
    width: `${DRAWER_WIDTH}px`,
    maxWidth: '84vw',
    background: 'rgba(255,255,255,0.97)',
    backdropFilter: 'blur(32px) saturate(150%)',
    WebkitBackdropFilter: 'blur(32px) saturate(150%)',
    boxShadow: '-1px 0 0 rgba(0,0,0,0.04), -4px 0 16px rgba(0,0,0,0.04), -12px 0 40px rgba(0,0,0,0.06)',
    transform: isClosing ? 'translateX(100%)' : 'translateX(0)',
    transition: `transform ${ANIMATION_DURATION}ms cubic-bezier(0.32, 0.72, 0, 1)`,
  };

  const backdropStyle: CSSProperties = {
    opacity: isClosing ? 0 : 1,
    transition: `opacity ${ANIMATION_DURATION}ms cubic-bezier(0.32, 0.72, 0, 1)`,
  };

  // Running index for stagger animation
  let globalIndex = 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-50 bg-black/20 backdrop-blur-[6px]
                   ${!isClosing ? 'motion-safe:animate-backdrop-fade' : ''}`}
        style={backdropStyle}
        onClick={handleCloseWithAnimation}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        ref={sidebarRef}
        className={`fixed top-0 right-0 bottom-0 z-50 flex flex-col
                   ${!isClosing ? 'motion-safe:animate-slide-in-from-right' : ''}`}
        style={drawerStyle}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* ─── Header ─────────────────────────────────────── */}
        <div
          className="flex items-center justify-between flex-shrink-0"
          style={{
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
            paddingLeft: '16px',
            paddingRight: '12px',
            paddingBottom: '12px',
          }}
        >
          {/* Brand */}
          <button
            onClick={handleBrandClick}
            className="flex items-center gap-2.5 min-h-[44px] px-1 rounded-xl
                       active:scale-[0.98] motion-safe:transition-transform duration-150
                       focus-visible:outline-none focus-visible:ring-2
                       focus-visible:ring-primary-200/60 focus-visible:ring-offset-2
                       touch-manipulation"
            aria-label="Go to Dashboard"
          >
            <img
              src="/logo.svg"
              alt=""
              aria-hidden="true"
              className="flex-shrink-0"
              style={{ width: '32px', height: '32px', borderRadius: '8px' }}
            />
            <span className="text-[17px] font-semibold text-gray-800 tracking-[-0.02em] select-none">
              Postpartner
            </span>
          </button>

          {/* Close button */}
          <button
            ref={closeButtonRef}
            onClick={handleCloseWithAnimation}
            className="w-8 h-8 min-w-[44px] min-h-[44px] flex items-center justify-center
                       rounded-full text-gray-400 hover:text-gray-500 hover:bg-black/[0.04]
                       active:bg-black/[0.06] active:scale-[0.94]
                       motion-safe:transition-all duration-150
                       focus-visible:outline-none focus-visible:ring-2
                       focus-visible:ring-primary-200/60 focus-visible:ring-offset-1
                       touch-manipulation"
            aria-label="Close navigation menu"
          >
            <X className="w-[18px] h-[18px]" strokeWidth={2.25} />
          </button>
        </div>

        {/* Hairline separator */}
        <div className="mx-4" style={{ height: '0.5px', background: 'rgba(0,0,0,0.06)' }} aria-hidden="true" />

        {/* ─── Navigation list ────────────────────────────── */}
        <nav
          className="flex-1 overflow-y-auto overscroll-contain px-3 pt-3 pb-2"
          role="navigation"
          aria-label="Site navigation"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {GROUPED_NAV.map((group, groupIndex) => {
            const showHeader = group.group !== 'primary' && group.group !== 'account';

            return (
              <div
                key={group.group}
                className={groupIndex > 0 ? 'mt-1' : ''}
                role="group"
                aria-label={GROUP_LABELS[group.group]}
              >
                {/* Group header (only for daily/library) */}
                {showHeader && (
                  <p
                    className="text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-400 px-3 pt-3 pb-1.5 select-none"
                    aria-hidden="true"
                  >
                    {GROUP_LABELS[group.group]}
                  </p>
                )}

                {/* Group separator for account section */}
                {group.group === 'account' && (
                  <div className="mx-3 mt-2 mb-1" style={{ height: '0.5px', background: 'rgba(0,0,0,0.05)' }} aria-hidden="true" />
                )}

                <ul role="list">
                  {group.items.map((item) => {
                    const idx = globalIndex++;
                    const isActive = isActiveRoute(location.pathname, item.path);
                    const Icon = item.icon;
                    const staggerDelay = Math.min(idx, MAX_STAGGER_ITEMS) * 18;

                    return (
                      <li
                        key={item.path}
                        style={{ animationDelay: isClosing ? '0ms' : `${staggerDelay}ms` }}
                        className={isClosing ? '' : 'motion-safe:animate-fade-in-up'}
                      >
                        <NavLink
                          to={item.path}
                          onClick={handleNavClick}
                          className={`group relative flex items-center gap-3 px-3 min-h-[46px]
                                     rounded-[var(--r-nav-row,10px)] touch-manipulation
                                     active:scale-[0.985] motion-safe:transition-all duration-150
                                     focus-visible:outline-none focus-visible:ring-2
                                     focus-visible:ring-primary-200/50 focus-visible:ring-offset-1
                                     ${isActive
                                       ? 'bg-black/[0.04]'
                                       : 'hover:bg-black/[0.02] active:bg-black/[0.04]'}`}
                          aria-current={isActive ? 'page' : undefined}
                          aria-label={item.ariaLabel || item.label}
                        >
                          {/* Active indicator bar */}
                          {isActive && (
                            <div
                              className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-full"
                              style={{ background: 'var(--accent-primary, #007AFF)' }}
                              aria-hidden="true"
                            />
                          )}

                          {/* Icon container */}
                          <div
                            className={`flex items-center justify-center flex-shrink-0 rounded-lg
                                       motion-safe:transition-colors duration-150
                                       ${isActive ? 'bg-black/[0.05]' : 'bg-transparent'}`}
                            style={{ width: '32px', height: '32px' }}
                          >
                            <Icon
                              className={`motion-safe:transition-colors duration-150 ${
                                isActive ? 'text-gray-800' : 'text-gray-400'
                              }`}
                              style={{ width: '18px', height: '18px' }}
                              strokeWidth={isActive ? 2 : 1.75}
                            />
                          </div>

                          {/* Labels */}
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-[15px] leading-tight tracking-[-0.01em]
                                         motion-safe:transition-colors duration-150 ${
                                isActive ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'
                              }`}
                            >
                              {item.label}
                            </p>
                            {item.description && (
                              <p
                                className={`text-[12px] truncate leading-tight mt-0.5 tracking-[-0.01em]
                                           motion-safe:transition-colors duration-150 ${
                                  isActive ? 'text-gray-500' : 'text-gray-400'
                                }`}
                              >
                                {item.description}
                              </p>
                            )}
                          </div>
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>

        {/* ─── Footer: Sign out ───────────────────────────── */}
        <div
          className="flex-shrink-0 px-3 pt-2 relative"
          style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom, 20px))' }}
        >
          {/* Hairline separator */}
          <div
            className="absolute left-4 right-4 top-0"
            style={{ height: '0.5px', background: 'rgba(0,0,0,0.06)' }}
            aria-hidden="true"
          />

          <button
            onClick={() => {
              triggerHaptic();
              setShowSignOutConfirm(true);
            }}
            className="flex items-center gap-3 min-h-[44px] px-3 w-full rounded-xl
                       text-gray-400 hover:text-red-500/80 hover:bg-red-50/40
                       active:bg-red-50/60 active:scale-[0.98]
                       motion-safe:transition-all duration-150
                       focus-visible:outline-none focus-visible:ring-2
                       focus-visible:ring-red-200/60 focus-visible:ring-offset-1
                       touch-manipulation"
            aria-label="Sign out of your account"
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-lg">
              <LogOut className="w-[18px] h-[18px]" strokeWidth={1.75} />
            </div>
            <span className="text-[15px] font-medium tracking-[-0.01em]">Sign out</span>
          </button>
        </div>
      </div>

      {/* Sign out confirmation modal */}
      <ConfirmModal
        isOpen={showSignOutConfirm}
        onClose={() => setShowSignOutConfirm(false)}
        onConfirm={handleSignOut}
        title="Sign out?"
        message="You can always sign back in to continue your journey."
        confirmText="Sign out"
        cancelText="Stay"
        loading={signingOut}
      />
    </>
  );
});
